import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useAuth0 } from '@auth0/auth0-react';
import { Loader } from '@clatter/ui';
import {
  fetchPages,
  fetchMicrosites,
  fetchPageTemplates,
  fetchBios,
} from '../../store';
import SiteMaker from '../../components/SiteMaker/SiteMaker';
import { pageFromStore, bioFromStore } from '../fromStore';
import SiteMakerContentHead from '../../components/SiteMaker/SiteMakerContentHead';
import FooterSettingsForm from '../../components/FooterSettingsForm/FooterSettingsForm';
import { selectAllPageTemplates } from '../../store/page-templates.slice';
import { fetchMicrositesSettings } from '../../store/microsites-settings.slice';

const FooterSettings = () => {
  const { pageId } = useParams();
  const { user, isAuthenticated, isLoading } = useAuth0();

  if (isLoading) {
    return <div>Loading authorization...</div>;
  }
  if (!isAuthenticated) {
    // shouldn't happen, as this component is only displayed in a ProtectedRoute
    return <div>Not Authorized.</div>;
  }

  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(fetchPages());
    dispatch(fetchMicrosites(user));
    dispatch(fetchPageTemplates());
    dispatch(fetchBios());
    dispatch(fetchMicrositesSettings());
  }, [dispatch]);

  const loading = useSelector(
    (state) =>
      state.pageTemplates.loadingStatus === 'loading' ||
      state.microsites.loadingStatus === 'loading' ||
      state.pages.loadingStatus === 'loading' ||
      state.bios.loadingStatus === 'loading' ||
      state.micrositesSettings.loadingStatus === 'loading',
  );

  const bios = useSelector((state) =>
    Object.values(state.bios.entities).map(bioFromStore),
  );

  const pageTemplates = useSelector(selectAllPageTemplates);

  const currentMicrosite = useSelector((state) => {
    if (state.pages.ids.indexOf[pageId] === -1) {
      return;
    }

    const siteId = state.microsites.ids.find((micrositeId) =>
      state.microsites.entities[micrositeId].pages.some(
        (page) => page.id === pageId,
      ),
    );

    if (!siteId || !state.microsites.entities[siteId]) {
      return;
    }

    return {
      ...state.microsites.entities[siteId],
      pages: state.microsites.entities[siteId].pages?.map((page) =>
        pageFromStore(page, pageTemplates),
      ),
    };
  });

  const currentPage = currentMicrosite?.pages?.find(({ id }) => id === pageId);

  const micrositesSettings = useSelector(
    ({ micrositesSettings }) => micrositesSettings.config,
  );

  if (loading) {
    return <Loader />;
  }

  if (
    !loading &&
    (!currentMicrosite || !currentMicrosite.pages || !currentPage)
  ) {
    return <p>Couldn't load current page</p>;
  }

  return (
    <SiteMaker site={currentMicrosite}>
      <SiteMakerContentHead
        title="Customize the footer"
        subhead="Select a client lead bio for your custom footer."
        pageId={pageId}
        siteName={currentMicrosite.name}
        pageName={currentPage.name}
      />
      <FooterSettingsForm
        micrositesSettings={micrositesSettings}
        currentMicrosite={currentMicrosite}
        currentPage={currentPage}
        bios={bios}
      />
    </SiteMaker>
  );
};

export default FooterSettings;
